.textField {
  /* This variable lets us use a box-shadow to increase border size without shifting content */
  --active-border-size-diff: calc(var(--active-border-size) - var(--default-border-size));
  --clear-button-size: 24px;

  color: var(--content-primary);
  display: block;
}

.label {
  color: var(--content-secondary);
  transition: all 0.1s ease-out;
  position: absolute;
  left: var(--horizontal-padding);
  font: var(--body-md-font);
  top: var(--inactive-padding-vertical);
}

.error .label {
  color: var(--content-status-error);
}

.large .label {
  font: var(--body-lg-font);
}

/* Position label when there's a left icon */
.inputBox:has(> .leftSlot) .label {
  left: calc(var(--horizontal-padding) + var(--spacing-2xl));
  top: var(--inactive-padding-vertical);
}

.inputBox:has(input:focus) .label,
.inputBox:has(input.input:not(:placeholder-shown)) .label {
  font: var(--micro-default-font);
  top: var(--active-padding-vertical);
  left: var(--horizontal-padding);
  transform: none;
}

.hint {
  color: var(--content-secondary);
  display: block;
  font: var(--body-md-font);
  letter-spacing: var(--body-md-letter-spacing);
  padding-left: var(--spacing-sm);
  margin-top: var(--spacing-xs);
}

.large .hint {
  margin-top: var(--spacing-sm);
}

.error > .hint {
  color: var(--content-status-error);
}

.inputBox {
  --vertical-padding: var(--spacing-md);
  --horizontal-padding: var(--spacing-md);
  --default-border-size: var(--border-width-input-default);
  --active-border-size: var(--border-width-input-focus);

  background: var(--surface-action-input-default);
  height: var(--size-input-height-md);
  border-radius: var(--radius-xl);
  border: var(--border-width-input-default) solid var(--border-action-input-default);
  color: var(--content-primary);
  cursor: text;
  display: flex;
  flex-direction: row;
  align-items: center;
  position: relative;
  font: var(--body-lg-font);
  padding-left: var(--horizontal-padding);
  padding-right: var(--horizontal-padding);
  box-sizing: border-box;
  transition:
    border-color 0.1s ease-out,
    box-shadow 0.1s ease-out;

  @media (width <= 768px) {
    font-size: var(--body-md-font-size);
  }
}

.leftSlot {
  display: flex;
  align-items: center;
  margin-right: var(--spacing-md);
}

.rightSlot {
  display: flex;
  align-items: center;
  position: absolute;
  margin-right: var(--spacing-xl);
  right: 0;
  top: 50%;
  transform: translateY(-50%);
}

.inputBox:has(> .leftSlot) {
  padding-left: var(--spacing-xl);
}

/* Show label when input is focused, even with left icon */
.inputBox:has(> .leftSlot):has(input:focus) .label {
  font: var(--micro-default-font);
  top: var(--active-padding-vertical);
  left: calc(var(--horizontal-padding) + var(--spacing-2xl));
}

/* Ensure the label is positioned correctly when focused with left icon */
.inputBox:has(> .leftSlot):has(input.input:not(:placeholder-shown)) .label {
  font: var(--micro-default-font);
  top: var(--active-padding-vertical);
  left: calc(var(--horizontal-padding) + var(--spacing-2xl));
}

.inputBox:has(.clearButton) {
  padding-right: calc(var(--clear-button-size) + var(--horizontal-padding) + 2px);
}

.small .inputBox {
  --text-height: var(--body-sm-line-height);
  --inactive-padding-vertical: 10px;
  --active-padding-vertical: 8px;
  --horizontal-padding: 12px;

  font: var(--body-sm-font);
  height: var(--size-input-height-sm);
  border-radius: var(--radius-lg);
}

.medium .inputBox {
  --inactive-padding-vertical: 14px;
  --active-padding-vertical: 8px;
  --text-height: var(--body-md-line-height);
  --horizontal-padding: 12px;

  font: var(--body-md-font);
  height: var(--size-input-height-md);
  border-radius: var(--radius-xl);
}

.large .inputBox {
  --inactive-padding-vertical: 19px;
  --active-padding-vertical: 13px;
  --text-height: var(--body-lg-line-height);
  --horizontal-padding: 18px;

  font: var(--body-lg-font);
  height: var(--size-input-height-lg);
  border-radius: var(--radius-2xl);
}

.error .inputBox {
  border-color: var(--border-action-input-error);
}

.inputBox:has(input:focus) {
  border-color: var(--border-action-input-focus);

  /* using box shadow instead of border width to avoid content-shift */
  box-shadow: 0 0 0 var(--active-border-size-diff) var(--border-action-input-focus);
}

.inputBox:has(.input:disabled) {
  background-color: var(--surface-action-disabled);
  border-color: var(--border-action-input-disabled);
  color: var(--content-input-disabled);
}

.textField:hover .inputBox:has(.input:not(:disabled)) {
  /* using box shadow instead of border width to avoid content-shift */
  box-shadow: 0 0 0 var(--active-border-size-diff) var(--border-default);
}

.textField.error:hover .inputBox:has(.input:not(:disabled)) {
  /* using box shadow instead of border width to avoid content-shift */
  box-shadow: 0 0 0 var(--active-border-size-diff) var(--border-action-input-error);
}

.input {
  appearance: none;
  background: none;
  border: none;
  flex: 1;
  margin: 0;
  min-height: var(--clear-button-size);
  outline: none;
  padding: 0;
  padding-top: calc(var(--micro-default-line-height) + var(--spacing-xs));
  color: inherit;
}

.input::-webkit-outer-spin-button,
.input::-webkit-inner-spin-button,
.input::-webkit-calendar-picker-indicator {
  appearance: none;
  display: none;
}

.clearButton {
  appearance: none;
  background: none;
  border: none;
  cursor: pointer;
  height: var(--clear-button-size);
  margin: 0;
  padding: 0;
  position: absolute;
  right: 0;
  top: 50%;
  transform: translateY(-50%);
  visibility: hidden;
  margin-right: var(--spacing-xl);
  width: var(--clear-button-size);
  color: var(--content-secondary);
}

/* disabling this rule here, since they are all applying the same style
 * (so specificity does not matter, AND:) it's easier to describe what's happening in this order */

/* stylelint-disable no-descending-specificity */

/* initially, only show the button when there is text in a focused or hovered input
 * note: :not(placeholder-shown) works because the component inserts an empty space when the placeholder is blank */
.input:not(:placeholder-shown):hover ~ .clearButton,
.input:not(:placeholder-shown):focus ~ .clearButton,
/* but we also need to keep the button visible if you are interacting with it */
.clearButton:hover,
.clearButton:focus,
.clearButton:active {
  visibility: visible;
}

/* hide the right slot when the clear button should be visible */
.input:not(:placeholder-shown):hover ~ .rightSlot,
.input:not(:placeholder-shown):focus ~ .rightSlot {
  visibility: hidden;
}

/* also hide the right slot when interacting with the clear button - this needs to work even after input is cleared */
.inputBox:has(.clearButton:hover) .rightSlot,
.inputBox:has(.clearButton:focus) .rightSlot,
.inputBox:has(.clearButton:active) .rightSlot {
  visibility: hidden;
}

/* stylelint-disable block-no-empty */

.medium {
}

.large {
}

/* stylelint-enable block-no-empty */
